import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useApiErrorMessage } from '@/api/errors'
import { assignLead, type LeadSummary } from '@/api/leads'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useColleagues } from '@/hooks/useColleagues'
import { userLabel } from '@/lib/userDisplay'

/** The Select needs a value, and an empty string is not one Radix accepts. */
const NOBODY = 'NOBODY'

/**
 * Handing one lead on.
 *
 * Owner or administrator only — seeing a lead is not the same as being free to pass it along, or
 * somebody could quietly hand back a lead they were given. The owner keeps seeing it either way;
 * assigning is not giving away, which is why this dialog says "übergeben" and not "abgeben".
 */
export function AssignLeadDialog({
  lead,
  onOpenChange,
  onAssigned,
}: {
  /**
   * The lead to hand on, or null when the dialog is closed. Only what the dialog reads, so a row
   * of the list and a lead open for editing can both be handed in.
   */
  lead: Pick<LeadSummary, 'id' | 'assigneeId' | 'companyName'> | null
  onOpenChange: (open: boolean) => void
  /** With whom the lead is assigned to now, as the server answered. */
  onAssigned: (assigneeId: string | null) => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const open = lead !== null
  const colleagues = useColleagues(open)
  const [chosen, setChosen] = useState(NOBODY)
  const [busy, setBusy] = useState(false)

  // Opened on a different lead — or closed and opened again: the dialog starts from where *that*
  // lead stands, not from where the last one did. Adjusted during the render that brings the lead
  // in, rather than in an effect, which would paint the previous answer first.
  const [openedFor, setOpenedFor] = useState<string | null>(null)
  // Both sides normalised to null: a closed dialog has no lead, and `undefined !== null` would
  // make this run on every render rather than on the renders where the lead actually changed.
  const openFor = lead?.id ?? null
  if (openFor !== openedFor) {
    setOpenedFor(openFor)
    setChosen(lead?.assigneeId ?? NOBODY)
  }

  async function save() {
    if (!lead || busy) {
      return
    }
    setBusy(true)
    try {
      const saved = await assignLead(lead.id, chosen === NOBODY ? null : chosen)
      toast.success(t('leadAssign.done', { name: lead.companyName }))
      onAssigned(saved.assigneeId)
      onOpenChange(false)
    } catch (error) {
      toast.error(errorMessage(error, t('leadAssign.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('leadAssign.title')}</DialogTitle>
          <DialogDescription>
            {t('leadAssign.description', { name: lead?.companyName ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label htmlFor="lead-assignee">{t('leadAssign.label')}</Label>
          <Select value={chosen} onValueChange={(next) => next && setChosen(next)}>
            <SelectTrigger id="lead-assignee" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {colleagues.map((colleague) => (
                <SelectItem
                  key={colleague.id}
                  value={colleague.id}
                  // Kept in the list so an existing assignment still has a name against it — it is
                  // just not somebody to hand more work to.
                  disabled={!colleague.assignable && colleague.id !== lead?.assigneeId}
                  title={colleague.assignable ? undefined : t('leadSelection.assignable')}
                >
                  {userLabel(colleague, colleague.id)}
                </SelectItem>
              ))}
              <SelectSeparator />
              <SelectItem value={NOBODY}>{t('leadAssign.nobody')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void save()} disabled={busy}>
            {t('leadAssign.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
